import { attr, belongsTo } from "@ember-data/model";
import { validateInclusion } from "ember-changeset-validations/validators";
import type { IntlService } from "ember-intl";
import type Patient from "parog-web/models/patient";
import type { TreatmentSectionWithColor } from "./base-surgery-survey-filled-journal-event";
import BaseSurgerySurveyFilledJournalEvent from "./base-surgery-survey-filled-journal-event";
//---- Automatically generated, don't update this part ----

type EatingRecoveryStringEnum = "0" | "1" | "2";

type ActivityRecoveryStringEnum = "0" | "1" | "2";

type NauseaOrVomitingStringEnum = "0" | "1" | "2" | "3";

type PainSinceBackHomeStringEnum = "0" | "1" | "2";

type DigestiveRecoveryStringEnum = "0" | "1" | "2";

type DifficultiesToUrinateStringEnum = "0" | "1" | "2";

type TemperatureLevelStringEnum = "0" | "1" | "2";

type BleedingOnWoundStringEnum = "0" | "1" | "2";

type SystolicBloodPressureStringEnum = "0" | "1";

type DiastolicBloodPressureStringEnum = "0" | "1";

type PulseStringEnum = "0" | "1" | "2";

/**
 * Represents a pre surgery survey filled related to RAAC onco.
 */
export default class PostSurgerySurveyFilledJournalEvent extends BaseSurgerySurveyFilledJournalEvent {
  static type = "post-surgery-survey-filled-journal-event";

  @attr("string") declare eatingRecovery: EatingRecoveryStringEnum;

  @attr("string") declare activityRecovery: ActivityRecoveryStringEnum;

  @attr("string") declare nauseaOrVomiting: NauseaOrVomitingStringEnum;

  @attr("string") declare painSinceBackHome: PainSinceBackHomeStringEnum;

  @attr("string") declare digestiveRecovery: DigestiveRecoveryStringEnum;

  @attr("string")
  declare difficultiesToUrinate: DifficultiesToUrinateStringEnum;

  @attr("string") declare temperatureLevel: TemperatureLevelStringEnum;

  @attr("string") declare bleedingOnWound: BleedingOnWoundStringEnum;

  @attr("boolean", {
    allowNull: true
  })
  declare woundInflammation: boolean;

  @attr("boolean", {
    allowNull: true
  })
  declare woundFlow: boolean;

  @attr("boolean", {
    allowNull: true
  })
  declare calfPain: boolean;

  @attr("string")
  declare systolicBloodPressure: SystolicBloodPressureStringEnum;

  @attr("string")
  declare diastolicBloodPressure: DiastolicBloodPressureStringEnum;

  @attr("string") declare pulse: PulseStringEnum;

  @attr("boolean", {
    allowNull: true
  })
  declare hasAlertingValue: boolean;

  @attr("string") declare originTaskIdentifier: string;

  @attr("number") declare score: number;

  static validations(intl: IntlService) {
    return {
      ...this.baseValidations(intl),
      systolicBloodPressure: validateInclusion({
        list: ["0", "1"],
        allowBlank: true,
        description: intl.t(
          "validations.post-surgery-survey-filled-journal-event.systolic-blood-pressure"
        )
      }),
      diastolicBloodPressure: validateInclusion({
        list: ["0", "1"],
        allowBlank: true,
        description: intl.t(
          "validations.post-surgery-survey-filled-journal-event.diastolic-blood-pressure"
        )
      }),
      pulse: validateInclusion({
        list: ["0", "1", "2"],
        allowBlank: true,
        description: intl.t(
          "validations.post-surgery-survey-filled-journal-event.pulse"
        )
      }),
      eatingRecovery: validateInclusion({
        list: ["0", "1", "2"],
        allowBlank: true,
        description: intl.t(
          "validations.post-surgery-survey-filled-journal-event.eating-recovery"
        )
      }),
      activityRecovery: validateInclusion({
        list: ["0", "1", "2"],
        allowBlank: true,
        description: intl.t(
          "validations.post-surgery-survey-filled-journal-event.activity-recovery"
        )
      }),
      nauseaOrVomiting: validateInclusion({
        list: ["0", "1", "2", "3"],
        allowBlank: true,
        description: intl.t(
          "validations.post-surgery-survey-filled-journal-event.nausea-or-vomiting"
        )
      }),
      painSinceBackHome: validateInclusion({
        list: ["0", "1", "2"],
        allowBlank: true,
        description: intl.t(
          "validations.post-surgery-survey-filled-journal-event.pain-since-back-home"
        )
      }),
      digestiveRecovery: validateInclusion({
        list: ["0", "1", "2"],
        allowBlank: true,
        description: intl.t(
          "validations.post-surgery-survey-filled-journal-event.digestive-recovery"
        )
      }),
      difficultiesToUrinate: validateInclusion({
        list: ["0", "1", "2"],
        allowBlank: true,
        description: intl.t(
          "validations.post-surgery-survey-filled-journal-event.difficulties-to-urinate"
        )
      }),
      temperatureLevel: validateInclusion({
        list: ["0", "1", "2"],
        allowBlank: true,
        description: intl.t(
          "validations.post-surgery-survey-filled-journal-event.temperature-level"
        )
      }),
      bleedingOnWound: validateInclusion({
        list: ["0", "1", "2"],
        allowBlank: true,
        description: intl.t(
          "validations.post-surgery-survey-filled-journal-event.bleeding-on-wound"
        )
      })
    };
  }

  //---- End of generated code ----

  static sections = [
    "eatingRecovery",
    "activityRecovery",
    "nauseaOrVomiting",
    "painSinceBackHome",
    "digestiveRecovery",
    "difficultiesToUrinate",
    "temperatureLevel",
    "bleedingOnWound",
    "woundInflammation",
    "woundFlow",
    "calfPain",
    "systolicBloodPressure",
    "diastolicBloodPressure",
    "pulse"
  ];

  get sectionsWithColor(): TreatmentSectionWithColor[] {
    const scoreValues = {
      eatingRecovery: {
        "0": 0,
        "1": 10,
        "2": 100
      },
      activityRecovery: {
        "0": 0,
        "1": 30,
        "2": 100
      },
      nauseaOrVomiting: {
        "0": 0,
        "1": 10,
        "2": 30,
        "3": 100
      },
      painSinceBackHome: {
        "0": 0,
        "1": 30,
        "2": 100
      },
      digestiveRecovery: {
        "0": 0,
        "1": 30,
        "2": 100
      },
      difficultiesToUrinate: {
        "0": 0,
        "1": 30,
        "2": 100
      },
      temperatureLevel: {
        "0": 0,
        "1": 30,
        "2": 100
      },
      bleedingOnWound: {
        "0": 0,
        "1": 30,
        "2": 100
      },
      woundInflammation: {
        true: 100,
        false: 0
      },
      woundFlow: {
        true: 100,
        false: 0
      },
      calfPain: {
        true: 100,
        false: 0
      },
      systolicBloodPressure: {
        "0": 100,
        "1": 0
      },
      diastolicBloodPressure: {
        "0": 100,
        "1": 0
      },
      pulse: {
        "0": 0,
        "1": 100,
        "2": 0
      }
    };

    return this.computeSectionsWithColor(
      PostSurgerySurveyFilledJournalEvent.sections,
      scoreValues
    );
  }

  @belongsTo("patient", {
    async: false,
    inverse: "postSurgerySurveyFilledJournalEvents"
  })
  declare patient: Patient;
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "post-surgery-survey-filled-journal-event": PostSurgerySurveyFilledJournalEvent;
  }
}
